import {
  reportUnexpectedError,
  BaseApplicationService,
} from "@graffer-inc/smart-apply";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../graphql/client";
import { toast } from "react-toastify";
import apiClient from "../../../apis/tenant-management/tenant-management-client";
import { OrgSamlSettingType } from "../../../domain/organization/org-saml-setting";
import FileSaver from "file-saver";

type mode = "VIEW" | "EDIT";

export type OrgSamlSettingServiceProps = {
  isLoading: boolean;
  setting: OrgSamlSettingType | null;
  mode: mode;
  initSetting: typeof OrgSamlSettingService.prototype.initSetting;
  setSetting: typeof OrgSamlSettingService.prototype.setSetting;
  uploadCertificateFile: typeof OrgSamlSettingService.prototype.uploadCertificateFile;
  downloadSAMLSPMetadataXML: typeof OrgSamlSettingService.prototype.downloadSAMLSPMetadataXML;
  setMode: typeof OrgSamlSettingService.prototype.setMode;
  deleteCertificateFile: typeof OrgSamlSettingService.prototype.deleteCertificateFile;
  fetchOrgSamlSetting: typeof OrgSamlSettingService.prototype.fetchOrgSamlSetting;
  createOrgSamlSetting: typeof OrgSamlSettingService.prototype.createOrgSamlSetting;
  updateOrgSamlSetting: typeof OrgSamlSettingService.prototype.updateOrgSamlSetting;
};
export class OrgSamlSettingService extends BaseApplicationService<OrgSamlSettingServiceProps> {
  state = {
    isLoading: false,
    setting: null as OrgSamlSettingType | null,
    mode: "VIEW" as mode,
    relayState: "",
    initSetting: this.initSetting.bind(this),
    setSetting: this.setSetting.bind(this),
    uploadCertificateFile: this.uploadCertificateFile.bind(this),
    downloadSAMLSPMetadataXML: this.downloadSAMLSPMetadataXML.bind(this),
    setMode: this.setMode.bind(this),
    deleteCertificateFile: this.deleteCertificateFile.bind(this),
    fetchOrgSamlSetting: this.fetchOrgSamlSetting.bind(this),
    createOrgSamlSetting: this.createOrgSamlSetting.bind(this),
    updateOrgSamlSetting: this.updateOrgSamlSetting.bind(this),
  };

  private setIsLoading(isLoading: boolean) {
    this.setState({ isLoading: isLoading });
  }

  initSetting(orgId: string) {
    const initialSetting = {
      id: "",
      orgId,
      samlSlug: "",
      idProviderSsoUrl: "",
      idProviderEntityId: "",
      certificateFileKey: "",
      relayState: "",
    };
    this.setState({
      setting: initialSetting,
      mode: "EDIT",
    });
  }

  setSetting(setting: OrgSamlSettingType) {
    this.setState({
      setting: setting,
    });
  }

  async uploadCertificateFile(file: File, dataUri: string) {
    if (!this.state.setting) {
      return;
    }
    try {
      const { data } = await apiClient.uploadCertificateFile(
        this.state.setting.orgId,
        file.name,
        dataUri,
      );
      this.setState({
        setting: {
          ...this.state.setting,
          certificateFileKey: data.filekey,
        },
      });
      toast.success("ファイルをアップロードしました。");
    } catch (e) {
      // TODO: pen形式でないファイルがアップロードされた場合のエラーをハンドリングして、メッセージを出し分ける
      reportUnexpectedError(e);
      toast.error("ファイルのアップロードに失敗しました。");
    }
  }

  async downloadSAMLSPMetadataXML(orgId: string) {
    if (!this.state.setting) {
      return;
    }

    try {
      const { data } = await apiClient.downloadSAMLSPMetadata(orgId);
      FileSaver.saveAs(data, "metadata.xml");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("メタデータファイルのダウンロードに失敗しました。");
    }
  }

  deleteCertificateFile() {
    if (!this.state.setting) {
      return;
    }
    this.setState({
      setting: {
        ...this.state.setting,
        certificateFileKey: "",
      },
    });
  }

  async fetchOrgSamlSetting(orgID: string) {
    this.setIsLoading(true);
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.orgSamlSetting({ orgID }, gqlHeader("q")),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data?.orgSamlSetting) {
        this.setState({
          setting: res.data.orgSamlSetting,
        });
      }
    } catch (e) {
      toast.error("SAML設定の取得に失敗しました。");
      reportUnexpectedError(e);
    } finally {
      this.setIsLoading(false);
    }
  }

  async createOrgSamlSetting(setting: OrgSamlSettingType) {
    this.setIsLoading(true);
    try {
      if (!this.state.setting) {
        return;
      }
      const res = await runGqlQueryV2(() =>
        gqlClient.createOrgSamlSetting(
          {
            req: {
              orgId: setting.orgId,
              samlSlug: setting.samlSlug,
              idProviderSsoUrl: setting.idProviderSsoUrl,
              idProviderEntityId: setting.idProviderEntityId,
              certificateFileKey: setting.certificateFileKey,
            },
          },
          gqlHeader("m"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data) {
        toast.success("SAML設定を作成しました");
        this.fetchOrgSamlSetting(setting.orgId);
      }
    } catch (e) {
      toast.error("SAML設定の作成に失敗しました。");
      reportUnexpectedError(e);
    } finally {
      this.setState({
        isLoading: false,
        mode: "VIEW",
      });
    }
  }

  async updateOrgSamlSetting(setting: OrgSamlSettingType) {
    this.setIsLoading(true);
    try {
      if (!this.state.setting) {
        return;
      }
      const res = await runGqlQueryV2(() =>
        gqlClient.updateOrgSamlSetting(
          {
            id: setting.id,
            req: {
              orgId: setting.orgId,
              samlSlug: setting.samlSlug,
              idProviderSsoUrl: setting.idProviderSsoUrl,
              idProviderEntityId: setting.idProviderEntityId,
              certificateFileKey: setting.certificateFileKey,
            },
          },
          gqlHeader("m"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data) {
        toast.success("SAML設定を更新しました");
      }
    } catch (e) {
      toast.error("SAML設定の更新に失敗しました。");
      reportUnexpectedError(e);
    } finally {
      this.setState({
        isLoading: false,
        mode: "VIEW",
      });
    }
  }

  setMode(mode: mode) {
    this.setState({ mode });
  }
}
